import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import t from 'utils/translate';
import LabelWithInfo from 'modules/shared/components/label-with-info';
import {
    selectCoreConfigurationSettings,
    passwordMeetsRequirements,
} from 'modules/settings/stores/company-settings-slice-handler';
import { changePassword, selectPasswordSaving } from 'modules/settings/stores/user-settings-slice-handler';
import { Autocomplete, Button, MenuItem, Paper } from '@mui/material';
import TextInput from 'modules/shared/components/text-input';
import LabelWithCheckbox from 'modules/shared/components/label-with-checkbox';

function SectionChangePassword() {
    const coreConfigurationSettings = useSelector(selectCoreConfigurationSettings);
    const passwordIsSaving = useSelector(selectPasswordSaving);

    const [showPassword, setShowPassword] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [passwords, setPasswords] = useState({ current: '', new: '', newConfirmed: '' });
    const [currentPasswordConfirmed, setCurrentPasswordConfirmed] = useState(false);
    const [newPasswordValidity, setNewPasswordValidity] = useState({
        minArabicNumeral: false,
        minLatinLowercase: false,
        minLatinUppercase: false,
        minLength: false,
        minSpecialChars: false,
    });

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const newPasswordInvalid = () => !Object.values(newPasswordValidity).every((option) => option === true);

    const requirementsLabels = {
        minArabicNumeral: 'settings_password_requirements_digit',
        minLatinLowercase: 'settings_password_requirements_lowercase',
        minLatinUppercase: 'settings_password_requirements_uppercase',
        minLength: 'settings_password_equirements_use_min',
        minSpecialChars: 'settings_password_requirements_special',
    };

    /**
     * @param {SyntheticBaseEvent} e
     * @param {'current'|'new'|'newConfirmed'} type
     */
    const handlePasswordUpdate = (e, type) => {
        const value = e.target.value;

        setPasswords({ ...passwords, [type]: value });

        if (type === 'new') {
            setNewPasswordValidity(passwordMeetsRequirements(value));
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!currentPasswordConfirmed) {
            setShowPassword(false);
            setCurrentPasswordConfirmed(true);
        } else {
            setFormSubmitted(true);
            if (newPasswordInvalid() || passwords.new !== passwords.newConfirmed) {
                return;
            }

            await changePassword(passwords.current, passwords.new);
            resetFormState();
        }
    };

    const resetFormState = useCallback(() => {
        setShowPassword(false);
        setPasswords({ current: '', new: '', newConfirmed: '' });
        setNewPasswordValidity(passwordMeetsRequirements(''));
        setCurrentPasswordConfirmed(false);
        setFormSubmitted(false);
    }, []);

    if (!coreConfigurationSettings.allowPasswordManagement) {
        return null;
    }

    return (
        <section className={classNames('settings-section', 'change-password-container')}>
            <div className="settings-section-title">
                <LabelWithInfo text={t('settings_password_title')} />
            </div>

            <form onSubmit={handleSubmit} className="change-password-form">
                {!currentPasswordConfirmed ? (
                    <>
                        <div className="section-content">
                            <div className="section-row">
                                <LabelWithInfo text={t('settings_password_current_password_label')} />
                                <TextInput
                                    value={passwords.current}
                                    className="change-password-input"
                                    onChange={(e) => handlePasswordUpdate(e, 'current')}
                                    icon={showPassword ? 'eye-closed' : 'eye-open'}
                                    iconClick={handleClickShowPassword}
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder={t('settings_password_current_password_placeholder')}
                                    data-test-id="current-password"
                                    autoComplete={'off'}
                                />
                            </div>

                            <div className="section-row row-reverse">
                                <Button type="submit" variant="contained" role="primary" disabled={!passwords.current}>
                                    {t('js_all_next')}
                                </Button>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="section-content">
                            <div className="section-row">
                                <LabelWithInfo text={t('settings_password_new_password_label')} />

                                <Autocomplete
                                    clearOnBlur={false}
                                    options={Object.keys(newPasswordValidity)}
                                    filterOptions={(option) => option}
                                    PaperComponent={({ children }) => (
                                        <Paper className="wcf-ui-password-requirements-list">
                                            <MenuItem disabled>
                                                <LabelWithInfo text={t('settings_password_requirements')} />
                                            </MenuItem>
                                            {children}
                                        </Paper>
                                    )}
                                    renderOption={(props, option) => (
                                        <MenuItem onClick={() => {}} key={props.key} disabled>
                                            <LabelWithCheckbox
                                                labelContent={t(requirementsLabels[option]).replace(
                                                    '%value',
                                                    coreConfigurationSettings[option],
                                                )}
                                                checked={newPasswordValidity[option]}
                                                onChange={() => {}}
                                                key={props.key}
                                            />
                                        </MenuItem>
                                    )}
                                    renderInput={(params) => (
                                        <TextInput
                                            {...params}
                                            value={passwords.new}
                                            onChange={(e) => handlePasswordUpdate(e, 'new')}
                                            icon={showPassword ? 'eye-closed' : 'eye-open'}
                                            iconClick={handleClickShowPassword}
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder={t('settings_password_new_password_placeholder')}
                                            error={formSubmitted && newPasswordInvalid()}
                                            helperText={
                                                formSubmitted &&
                                                newPasswordInvalid() &&
                                                t('settings_password_requirements_no_match')
                                            }
                                            autoComplete={'off'}
                                        />
                                    )}
                                />
                            </div>

                            <div className="section-row">
                                <LabelWithInfo text={t('settings_password_confirm_password_label')} />
                                <TextInput
                                    value={passwords.newConfirmed}
                                    onChange={(e) => handlePasswordUpdate(e, 'newConfirmed')}
                                    icon={showPassword ? 'eye-closed' : 'eye-open'}
                                    iconClick={handleClickShowPassword}
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder={t('settings_password_confirm_password_placeholder')}
                                    error={formSubmitted && passwords.newConfirmed !== passwords.new}
                                    helperText={
                                        formSubmitted &&
                                        passwords.newConfirmed !== passwords.new &&
                                        t('settings_passwords_no_match')
                                    }
                                    autoComplete={'off'}
                                />
                            </div>

                            <div className="section-row form-buttons">
                                <Button variant="contained" role="cancel" color="secondary" onClick={resetFormState}>
                                    {t('js_all_cancel')}
                                </Button>
                                <Button type="submit" variant="contained" role="primary" disabled={passwordIsSaving}>
                                    {t('settings_password_title')}
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </form>
        </section>
    );
}

export default SectionChangePassword;
